import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Breadcrumbs,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

const FORM_WIDTH = 500;

export const TitleRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
});

export const ItemTitle = styled(Typography)({});
ItemTitle.defaultProps = { variant: 'body2', component: 'div' };

export const RealmText = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));
RealmText.defaultProps = { variant: 'body2', component: 'div' };

export const RealmTypeIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 36,
  flexShrink: 0,
  color: theme.palette.text.secondary,
  '& .MuiSvgIcon-root, & .MuiIcon-root': {
    fontSize: 20,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 28,
  },
}));

export const SelectControl = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 42,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    minWidth: 28,
  },
}));

export const ToggleButton = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  marginLeft: theme.spacing(2),
}));

export const FilterToggleButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
  minWidth: 'auto',
}));

export const ItemsIndent = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(3),
}));

export const FilterSelectedWrap = styled(Box)({
  width: 300,
});

export const SelectedSummary = styled('div')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.success.main,
  fontWeight: 'bold',
}));

export const ListWrap = styled('div')(({ theme }) => ({
  margin: '0 auto',
  width: '100%',
  maxWidth: FORM_WIDTH,
  padding: `0 ${theme.spacing(6)}`,
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));

export const ListHeader = styled('div')(({ theme }) => ({
  fontSize: 13,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  '& .titleDiv': {
    margin: `${theme.spacing(1)} 0`,
  },
  // List rows go edge-to-edge on small screens; keep breadcrumbs/back inset
  // to match ListItemButton gutters.
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const RealmBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  fontSize: 13,
  '& .MuiTypography-root, & .MuiLink-root, & button': {
    fontSize: 13,
  },
}));

export const BreadcrumbCurrent = styled(Typography)({
  fontWeight: 600,
});

export const BackButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(-1),
}));

export const RealmList = styled(List, {
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'disableListScroll',
})(({ theme, loading, disableListScroll }) => ({
  width: '100%',
  maxWidth: FORM_WIDTH,
  boxSizing: 'border-box',
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: 0,
  opacity: loading ? 0.7 : 1,
  ...(disableListScroll
    ? {
        height: 'auto',
        overflow: 'visible',
      }
    : {
        height: 'inherit',
        overflowX: 'hidden',
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
          height: 'calc(100vh - 200px)',
        },
      }),
}));

export const RealmListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'selected',
})(({ theme, loading, selected }) => {
  const selectedBg = alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity);
  const selectedHoverBg = alpha(
    theme.palette.primary.main,
    theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
  );

  return {
    width: '100%',
    maxWidth: FORM_WIDTH,
    boxSizing: 'border-box',
    borderBottom: `1px solid ${theme.palette.divider}`,
    pointerEvents: loading ? 'none' : undefined,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    ...(selected && {
      backgroundColor: selectedBg,
      '&:hover': {
        backgroundColor: selectedHoverBg,
        '@media (hover: none)': {
          backgroundColor: selectedBg,
        },
      },
    }),
  };
});

export const RealmListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'navigateOnly',
})(({ theme, navigateOnly }) => ({
  minHeight: 48,
  flex: '1 1 0%',
  minWidth: 0,
  overflow: 'hidden',
  paddingRight: navigateOnly ? theme.spacing(2) : undefined,
  // Row owns hover/selected; keep this transparent so the fill isn't clipped
  // to the name column. `&&` beats MUI ListItemButton under injectFirst.
  '&&, &&:hover, &&.Mui-selected, &&.Mui-selected:hover': {
    backgroundColor: 'transparent',
  },
}));

/** Name column: takes remaining row space and scrolls horizontally when too long. */
export const RealmNameText = styled(ListItemText)(({ theme }) => ({
  // flex-basis 0% so width comes from remaining space, not from the long name.
  flex: '1 1 0%',
  minWidth: 0,
  maxWidth: '100%',
  marginTop: 0,
  marginBottom: 0,
  marginRight: theme.spacing(1),
  overflow: 'hidden',
  '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
    display: 'block',
    maxWidth: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
}));

export const DigDeeperButton = styled(Button)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(0.5),
  marginRight: theme.spacing(2),
  flexShrink: 0,
  minWidth: 0,
  alignSelf: 'center',
  '&&, &&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
}));

export const DigDeeperHint = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  flexShrink: 0,
  color: theme.palette.text.secondary,
  pointerEvents: 'none',
}));

export const ChildCount = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  minWidth: '1.5em',
  display: 'inline-flex',
  justifyContent: 'flex-end',
  '& .MuiCircularProgress-root': {
    width: '14px !important',
    height: '14px !important',
  },
}));

export const ItemEditContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: theme.spacing(1),
}));

export const EditRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
});

export const EditButtonRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginTop: theme.spacing(1),
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

export const ArchiveRow = styled('div')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.2),
  display: 'flex',
  flexDirection: 'row',
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  marginBottom: theme.spacing(2),
  alignItems: 'center',
}));

export const ArchiveText = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  fontSize: 12,
});

export const ArchiveButtonRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));
